import CustomTooltip from '@gui/components/generic/CustomTooltip';
import LayerTree from '@gui/components/panels/data/LayerTree';
import SidePanelDialog from '@gui/components/panels/settings/SidePaneDialog';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { Box, Button, Checkbox, MenuItem, Select, Slider, Stack, Tooltip, Typography } from '@mui/material';
import { InfoCircle } from '@untitledui-pro/icons/line';
import { DeepPartial, DesignSchemeSetType, FacadeDirectionMode, FacadeType, FloorBasedDesignState, LayerKind } from '@visualizer/types';
import { useEffect, useState } from 'react';
import { getGrid } from 'store/LoadingStore';
import { GroupLabel } from '../DesignSchemeView';
import { addMixedLayerToCollection } from '../DesignSchemeViewUtils';
import { getSentence } from '@gui/constants/sentences';
import { FloorBasedComponentsView } from './FloorBasedComponentsView';
import {
    CollapsibleGroupLabel,
    formatOneDecimal,
    optionalSliderLabelSx,
    sliderInteractionProps,
    sliderRowSx,
    sliderSx,
    sliderUnitSx,
    tinyLabelSx,
    tinySelectSx,
} from './FloorBasedView.shared';

interface FloorBasedFacadesViewProps {
    setId: string;
    footprintId: string;
    schemeId: string;
    floorHeight: number;
    floorThickness: number;
    hideFacadesEnabled: boolean;
    updateFloorBased: (partial: DeepPartial<FloorBasedDesignState>) => void;
}

const getFacadeTypeTooltip = (facadeType: FacadeType): string => {
    switch (facadeType) {
        case FacadeType.TRANSPARENT:
            return getSentence('DesignSchemes.FloorBased.Facade.Transparent.Tooltip');
        case FacadeType.OPAQUE:
            return getSentence('DesignSchemes.FloorBased.Facade.Opaque.Tooltip');
        case FacadeType.TRANSPARENT_MAIN_DIRECTION:
            return 'Renders as transparent only the facades that face the selected Direction input (Volume/Floor/Facade Average); the direction is computed using only points close to the facade shell (CloseSP). In Full Site, exterior points are missing at boundaries, so internal ones are used instead.';
        case FacadeType.FACADE_WITH_OPENINGS:
            return 'Renders facades as opaque by default and opens full-height transparent sections wherever at least one facade sensor is assigned to that facade.';
        case FacadeType.HORIZONTALLY_ORIENTED:
            return getSentence('DesignSchemes.FloorBased.Facade.Horizontal.Tooltip');
        default:
            return '';
    }
};

const facadeTooltipComponentsProps = {
    popper: {
        modifiers: [{ name: 'flip', enabled: false }],
    },
};

const getFacadeTypeLabel = (facadeType: FacadeType): string => {
    switch (facadeType) {
        case FacadeType.TRANSPARENT:
            return 'Fully Transparent';
        case FacadeType.OPAQUE:
            return 'Fully Opaque';
        case FacadeType.TRANSPARENT_MAIN_DIRECTION:
            return 'Transparent in Main Direction';
        case FacadeType.FACADE_WITH_OPENINGS:
            return 'Facade with Openings';
        case FacadeType.HORIZONTALLY_ORIENTED:
            return 'Horizontally Oriented';
        default:
            return '';
    }
};

const renderFacadeMenuLabel = (label: string, facadeType: FacadeType) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
        <Tooltip placement="top" title={getFacadeTypeTooltip(facadeType)} componentsProps={facadeTooltipComponentsProps}>
            <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
                <InfoCircle className="icon-size-small" />
            </span>
        </Tooltip>
        <Typography sx={{ fontSize: '10px' }}>{label}</Typography>
    </Box>
);

// =====================================
// MAIN COMPONENT
// =====================================

export const FloorBasedFacadesView: React.FC<FloorBasedFacadesViewProps> = ({
    setId,
    footprintId,
    schemeId,
    floorHeight,
    floorThickness,
    hideFacadesEnabled,
    updateFloorBased,
}) => {
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();

    // Zustand Variables:
    const designSchemes = useDesignSchemesBucket((s) => s);
    const setType = designSchemes.sets?.[setId]?.type;
    const isFullSite = setType === DesignSchemeSetType.FULL_SITE;
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const floorBasedState = scheme.floorBased;

    // Defaults:
    const defaults = designSchemes.defaults.floorBased;

    // Facade Settings:
    const facadeType = floorBasedState?.facadeState.type ?? defaults.facadeState.type;
    const directionMode = floorBasedState?.facadeState.directionMode ?? defaults.facadeState.directionMode;
    const horizontalLength = floorBasedState?.facadeState.horizontalLength?.value ?? defaults.facadeState.horizontalLength.value;
    const horizontalFuseCoplanar = floorBasedState?.facadeState.horizontalFuseCoplanar ?? defaults.facadeState.horizontalFuseCoplanar;
    const showFacadeFullSiteWarning =
        isFullSite &&
        (facadeType === FacadeType.FACADE_WITH_OPENINGS ||
            facadeType === FacadeType.TRANSPARENT_MAIN_DIRECTION ||
            facadeType === FacadeType.HORIZONTALLY_ORIENTED);

    // React State:
    const [layerDialogOpen, setLayerDialogOpen] = useState(false);
    const [selectedFacadeLayerName, setSelectedFacadeLayerName] = useState<string>('Select Layer');
    const [layerTreeRefresh, setLayerTreeRefresh] = useState(0);
    const [sectionsOpen, setSectionsOpen] = useState({
        facades: false,
        components: false,
    });

    // Handlers:
    const handleCloseLayerTree = () => setLayerDialogOpen(false);

    const handleOpenLayerTree = () => {
        addMixedLayerToCollection(); // Make sure Mixer is in the layerTree.
        setLayerTreeRefresh((k) => k + 1); // Force refresh LayerTree to reflect changes in layer collection.
        setLayerDialogOpen(true);
    };

    const handleLayerClick = (layerId: string) => {
        const grid = getGrid();
        if (!grid) return;

        const newLayer = grid.searchLayerByUID(layerId);
        if (!newLayer) return;

        designSchemesController.updateSchemeLayer(setId, footprintId, schemeId, LayerKind.FACADE, newLayer.getUID());
        setSelectedFacadeLayerName(newLayer.name);

        handleCloseLayerTree();
    };

    // Collapsable Sections:
    const toggleSection = (section: 'facades' | 'components') => {
        setSectionsOpen((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    // =====================================
    // USE EFFECTS
    // =====================================

    useEffect(() => {
        const ds = designSchemesController.getDesignScheme(schemeId);
        if (!ds) return;

        const facadeLayer = ds.getLayer(LayerKind.FACADE);
        setSelectedFacadeLayerName(facadeLayer?.name ?? 'Select Layer');
    }, [designSchemesController, schemeId]);

    // =====================================
    // ACTUAL HTML
    // =====================================

    return (
        <>
            <SidePanelDialog open={layerDialogOpen} onClose={handleCloseLayerTree} side="right" title="Layer Linked to Facades">
                <Box sx={{ overflow: 'auto' }}>
                    <LayerTree key={layerTreeRefresh} onClick={handleLayerClick} />
                </Box>
            </SidePanelDialog>

            <CollapsibleGroupLabel label="Facades" open={sectionsOpen.facades} onToggle={() => toggleSection('facades')} />
            {sectionsOpen.facades && !hideFacadesEnabled && (
                <>
                    {/* CloseToDS Warning for Full Site */}
                    {showFacadeFullSiteWarning && (
                        <Stack direction="row" sx={{ color: 'error.main', minWidth: 0, mt: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 0 }}>
                                Warning: In Full Site, facade proximity filtering can be unreliable because sensor points may be missing
                                near analysis-area boundaries and after facade-facing filtering.
                            </Typography>
                        </Stack>
                    )}

                    {/* Facade Layer Selector */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography sx={tinyLabelSx}>Connected Layer</Typography>
                        <Button variant="outlined" onClick={handleOpenLayerTree} fullWidth>
                            {selectedFacadeLayerName}
                        </Button>
                    </Box>

                    {/* Facade Type */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mt: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography sx={tinyLabelSx}>Type</Typography>

                        <Select
                            data-testid="ds-facade-select"
                            size="small"
                            value={facadeType ?? FacadeType.TRANSPARENT}
                            renderValue={(value) => getFacadeTypeLabel(value as FacadeType)}
                            onChange={(e) =>
                                updateFloorBased({
                                    facadeState: {
                                        type: e.target.value as FacadeType,
                                    },
                                })
                            }
                            sx={tinySelectSx}
                        >
                            <MenuItem value={FacadeType.TRANSPARENT}>
                                {renderFacadeMenuLabel('Fully Transparent', FacadeType.TRANSPARENT)}
                            </MenuItem>
                            <MenuItem data-testid="ds-facade-opaque" value={FacadeType.OPAQUE}>
                                {renderFacadeMenuLabel('Fully Opaque', FacadeType.OPAQUE)}
                            </MenuItem>
                            <MenuItem data-testid="ds-facade-transparent-main-direction" value={FacadeType.TRANSPARENT_MAIN_DIRECTION}>
                                {renderFacadeMenuLabel('Transparent in Main Direction', FacadeType.TRANSPARENT_MAIN_DIRECTION)}
                            </MenuItem>
                            <MenuItem data-testid="ds-facade-openings" value={FacadeType.FACADE_WITH_OPENINGS}>
                                {renderFacadeMenuLabel('Facade with Openings', FacadeType.FACADE_WITH_OPENINGS)}
                            </MenuItem>
                            <MenuItem data-testid="ds-facade-horizontal" value={FacadeType.HORIZONTALLY_ORIENTED}>
                                {renderFacadeMenuLabel('Horizontally Oriented', FacadeType.HORIZONTALLY_ORIENTED)}
                            </MenuItem>
                        </Select>
                    </Box>

                    {/* Facade Direction */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <CustomTooltip side="right" title={getSentence('DesignSchemes.FloorBased.Direction.Tooltip')} mode="icon">
                            <Typography sx={tinyLabelSx}>Direction</Typography>
                        </CustomTooltip>

                        <Select
                            size="small"
                            value={directionMode}
                            onChange={(e) =>
                                updateFloorBased({
                                    facadeState: {
                                        directionMode: e.target.value as FacadeDirectionMode,
                                    },
                                })
                            }
                            sx={tinySelectSx}
                        >
                            <MenuItem value={FacadeDirectionMode.VOLUME_AVERAGE}>Volume Average</MenuItem>
                            <MenuItem value={FacadeDirectionMode.FLOOR_AVERAGE}>Floor Average</MenuItem>
                            <MenuItem value={FacadeDirectionMode.FACADE_AVERAGE}>Facade Average</MenuItem>
                        </Select>
                    </Box>

                    {facadeType === FacadeType.HORIZONTALLY_ORIENTED && (
                        <>
                            {/* Horizontal Facade Settings */}
                            <GroupLabel label="Horizontal Settings" />
                            <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Segment Width
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-horizontal-length-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={horizontalLength}
                                    min={defaults.facadeState.horizontalLength.minValue}
                                    max={defaults.facadeState.horizontalLength.maxValue}
                                    step={0.05}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                horizontalLength: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            {/* Fuse Coplanar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Checkbox
                                    data-testid="ds-horizontal-fuse-coplanar-checkbox"
                                    size="small"
                                    checked={horizontalFuseCoplanar}
                                    onChange={(e) =>
                                        updateFloorBased({
                                            facadeState: {
                                                horizontalFuseCoplanar: e.target.checked,
                                            },
                                        })
                                    }
                                    sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                                />
                                <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Fuse Coplanar</Typography>
                            </Box>
                        </>
                    )}
                </>
            )}

            {/** ===================================== */}
            {/** COMPONENTS */}
            {/** ===================================== */}

            {facadeType !== FacadeType.HORIZONTALLY_ORIENTED && (
                <FloorBasedComponentsView
                    setId={setId}
                    footprintId={footprintId}
                    schemeId={schemeId}
                    floorHeight={floorHeight}
                    floorThickness={floorThickness}
                    hideFacadesEnabled={hideFacadesEnabled}
                    componentSectionOpen={sectionsOpen.components}
                    onToggle={() => toggleSection('components')}
                    updateFloorBased={updateFloorBased}
                />
            )}
        </>
    );
};
